import { useEffect, useMemo, useState } from 'react';
import type {
  AIPlatformLF, Client, LfAIAnalysisGroup,
  LfAIScan, LfAIScanFull, LfScan,
} from '../types';
import { getLfAIScan } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { isClientAlias, isJunkCompetitorLine } from '../lib/clientMatch';
import {
  GBIBtn, GBICard, Icon, T, sharedStyles,
} from './Shared';

interface AIScanPanelProps {
  client: Client;
  aiScans: LfAIScan[];
  aiKeywords: string[];                 // Admin-managed prompt list
  lfScans: LfScan[];                    // Used only for fallback display context
  readOnly?: boolean;
  /** GBP full display_name (contains CJK aliases). Used to recognise the client
   *  under any alias in the competitor list. */
  placeDisplayName?: string | null;
  // Pull latest results from Local Falcon's Auto-Scan reports into D1.
  // We don't trigger new scans from this app — those run on a schedule
  // configured directly in the Local Falcon dashboard.
  onSync?: () => Promise<void>;
}

// Display labels + brand colors per platform.
const PLATFORM_LABEL: Record<AIPlatformLF, string> = {
  chatgpt: 'ChatGPT',
  gemini:  'Gemini',
  gaio:    'Google AI Overview',
};

const PLATFORMS: AIPlatformLF[] = ['chatgpt', 'gemini', 'gaio'];

const platformColor = (p: AIPlatformLF): string => T[p];

// LF embeds HTML tags (<strong>, <span>) inside analysis text. Strip for plain display.
function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// LF returns analysis sections as either string, string[], or {major, minor}.
// Normalize to a flat string[] of clean lines.
function normalizeAnalysisLines(
  v: string | LfAIAnalysisGroup | string[] | undefined | null,
): string[] {
  if (!v) return [];
  if (typeof v === 'string') return [stripHtml(v)].filter(Boolean);
  if (Array.isArray(v)) return v.map(s => stripHtml(String(s))).filter(Boolean);
  // Group object: combine major + minor in display order, major first.
  const out: string[] = [];
  const push = (g: string | string[] | false | undefined) => {
    if (g === false || g == null) return;
    if (typeof g === 'string') {
      const t = stripHtml(g);
      if (t) out.push(t);
    } else if (Array.isArray(g)) {
      for (const s of g) {
        const t = stripHtml(String(s));
        if (t) out.push(t);
      }
    }
  };
  push(v.major);
  push(v.minor);
  return out;
}

export function AIScanPanel({
  client, aiScans, aiKeywords, readOnly = false, placeDisplayName, onSync,
}: AIScanPanelProps) {
  const isMobile = useIsMobile();
  // Source of truth is the admin-managed `aiKeywords` list. We also include any
  // keyword that has scan data but isn't on the list (defensive — should be
  // impossible because DELETE cascades, but handles transient inconsistency).
  const keywords = useMemo(() => {
    const set = new Set<string>(aiKeywords);
    for (const s of aiScans) set.add(s.keyword);
    return Array.from(set);
  }, [aiKeywords, aiScans]);

  // Default selection: the first keyword that has any AI scan rows; else first overall.
  const initialKeyword = useMemo(() => {
    const withData = aiScans[0]?.keyword;
    return withData ?? keywords[0] ?? '';
  }, [aiScans, keywords]);

  const [activeKeyword, setActiveKeyword] = useState<string>(initialKeyword);
  useEffect(() => { if (!activeKeyword && initialKeyword) setActiveKeyword(initialKeyword); }, [initialKeyword, activeKeyword]);

  // Metadata for the active keyword × all platforms (no sources yet).
  const activeMetas = useMemo(() => {
    const byPlatform = new Map<AIPlatformLF, LfAIScan>();
    for (const s of aiScans) {
      if (s.keyword.toLowerCase() === activeKeyword.toLowerCase()) {
        byPlatform.set(s.platform, s);
      }
    }
    return PLATFORMS.map(p => ({ platform: p, meta: byPlatform.get(p) ?? null }));
  }, [aiScans, activeKeyword]);

  // Lazy-load full data (sources + ai_analysis) for active keyword's 5 platforms.
  const [fullByPlatform, setFullByPlatform] = useState<Map<AIPlatformLF, LfAIScanFull>>(new Map());
  const [loadingFull, setLoadingFull] = useState(false);

  useEffect(() => {
    if (!activeKeyword) return;
    const haveAny = activeMetas.some(m => m.meta);
    if (!haveAny) { setFullByPlatform(new Map()); return; }

    let cancelled = false;
    setLoadingFull(true);
    Promise.all(
      activeMetas
        .filter(m => m.meta)
        .map(async m => {
          try {
            const full = await getLfAIScan(client.id, activeKeyword, m.platform);
            return [m.platform, full] as const;
          } catch {
            return null;
          }
        }),
    ).then(results => {
      if (cancelled) return;
      const next = new Map<AIPlatformLF, LfAIScanFull>();
      for (const r of results) if (r) next.set(r[0], r[1]);
      setFullByPlatform(next);
    }).finally(() => { if (!cancelled) setLoadingFull(false); });

    return () => { cancelled = true; };
  }, [client.id, activeKeyword, activeMetas]);

  const lastSyncAgo = useMemo(() => {
    if (aiScans.length === 0) return null;
    const latest = Math.max(...aiScans.map(s => s.fetchedAt));
    return relativeTime(Date.now() - latest);
  }, [aiScans]);

  const [syncing, setSyncing] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const handleSync = async () => {
    if (!onSync) return;
    setSyncing(true);
    setActionMsg(null);
    try {
      await onSync();
      setActionMsg('AI results refreshed.');
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
    }
  };

  // Empty state — no AI scans synced yet.
  if (aiScans.length === 0) {
    return (
      <div style={{ ...containerStyle, padding: isMobile ? 14 : 28 }}>
        <Header
          clientName={client.name}
          lastSyncAgo={null}
          readOnly={readOnly}
          onSync={onSync ? handleSync : undefined}
          syncing={syncing}
        />
        {actionMsg && <ActionMessage text={actionMsg} />}
        <GBICard style={{ textAlign: 'center', padding: 36 }}>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 16, fontWeight: 600, marginBottom: 6, color: T.fgPrimary }}>
            No AI scans yet
          </div>
          <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 16, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            {readOnly
              ? 'AI visibility data will appear here on the next weekly sync.'
              : 'Refresh AI results to pull the latest completed weekly scan results into the dashboard.'}
          </div>
          {!readOnly && aiKeywords.length === 0 && (
            <div style={{ fontSize: 12, color: T.amber, fontWeight: 600 }}>
              First add at least one prompt in Settings → AI Scan keywords.
            </div>
          )}
        </GBICard>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, padding: isMobile ? 14 : 28 }}>
      <Header
        clientName={client.name}
        lastSyncAgo={lastSyncAgo}
        readOnly={readOnly}
        onSync={onSync ? handleSync : undefined}
        syncing={syncing}
      />

      {actionMsg && <ActionMessage text={actionMsg} />}

      {/* Keyword tabs */}
      {keywords.length > 1 && (
        <KeywordTabs
          keywords={keywords}
          active={activeKeyword}
          onChange={setActiveKeyword}
          aiScans={aiScans}
        />
      )}

      {/* Aggregate 3-metric card */}
      <KeywordMetrics aiScans={aiScans} active={activeKeyword} />

      {/* Visibility bar chart */}
      <SaivComparison metas={activeMetas} keyword={activeKeyword} />

      {/* Competitors AI named */}
      <CompetitorsNamed metas={activeMetas} fullByPlatform={fullByPlatform} keyword={activeKeyword} loading={loadingFull} clientName={client.name} placeDisplayName={placeDisplayName} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Header

function Header({
  clientName, lastSyncAgo, readOnly, onSync, syncing,
}: {
  clientName: string;
  lastSyncAgo: string | null;
  readOnly: boolean;
  onSync?: () => void;
  syncing: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
      <div>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 4 }}>
          AI Visibility Report
        </div>
        <div style={{ fontSize: 13, color: T.fgSecondary }}>
          {clientName} — how AI search engines recommend your business
          {lastSyncAgo && <> · last refreshed <span style={{ color: T.cyan }}>{lastSyncAgo}</span></>}
        </div>
      </div>
      {!readOnly && onSync && (
        <GBIBtn variant="primary" onClick={onSync} disabled={syncing}>
          <Icon name="globe" size={14} color={T.bgVoid} /> {syncing ? 'Refreshing…' : 'Refresh AI results'}
        </GBIBtn>
      )}
    </div>
  );
}

function ActionMessage({ text }: { text: string }) {
  return (
    <div style={{
      marginBottom: 16,
      padding: '8px 12px',
      background: T.bgSubtle,
      border: `1px solid ${T.border}`,
      borderRadius: 6,
      fontFamily: sharedStyles.fontMono,
      fontSize: 12,
      color: T.fgSecondary,
    }}>
      {text}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Keyword tabs

function KeywordTabs({
  keywords, active, onChange, aiScans,
}: {
  keywords: string[]; active: string; onChange: (k: string) => void; aiScans: LfAIScan[];
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: T.fgMuted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        Tracked prompts
        <span style={{
          fontSize: 9, fontWeight: 600, letterSpacing: 0, textTransform: 'none',
          color: T.cyan, background: 'rgba(18,181,196,0.10)',
          border: `1px solid ${T.cyan}`, borderRadius: 10, padding: '1px 8px',
        }}>👆 click to switch</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {keywords.map((k, i) => {
          const isActive = k === active;
          const matching = aiScans.filter(s => s.keyword.toLowerCase() === k.toLowerCase());
          const count = matching.length;
          // Aggregate SAIV across the 3 platforms (max 3 rows). null entries = not yet scanned.
          const avgSaiv = matching.length > 0
            ? matching.reduce((sum, s) => sum + (s.saiv ?? 0), 0) / matching.length
            : null;
          return (
            <button
              key={k}
              onClick={() => onChange(k)}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto auto',
                gap: 14,
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 10,
                border: isActive ? `2px solid ${T.cyan}` : `1px solid ${T.border}`,
                background: isActive ? 'rgba(18,181,196,0.07)' : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 120ms',
                boxShadow: isActive ? '0 2px 10px rgba(18,181,196,0.18)' : '0 1px 2px rgba(0,0,0,0.03)',
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = T.cyan; e.currentTarget.style.background = T.bgSubtle; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = '#fff'; } }}
            >
              {/* Number badge — signals "switchable options 1/2/3" */}
              <span style={{
                width: 24, height: 24, borderRadius: 7,
                background: isActive ? T.cyan : T.bgSubtle,
                color: isActive ? '#fff' : T.fgMuted,
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: isActive ? 'none' : `1px solid ${T.border}`,
              }}>{i + 1}</span>
              <span style={{
                fontFamily: sharedStyles.fontDisplay,
                fontSize: 14,
                fontWeight: isActive ? 700 : 600,
                color: isActive ? T.fgPrimary : T.fgSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                "{k}"
              </span>
              <span style={{
                fontFamily: sharedStyles.fontMono, fontSize: 11, fontWeight: 700,
                color: avgSaiv == null ? T.fgMuted : avgSaiv >= 50 ? T.green : avgSaiv > 0 ? T.amber : T.magenta,
                minWidth: 60, textAlign: 'right',
              }}>
                {avgSaiv == null ? '—' : `Avg ${avgSaiv.toFixed(0)}%`}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '3px 8px', borderRadius: 9999,
                background: count === 3 ? 'rgba(52,168,83,0.12)' : count > 0 ? 'rgba(245,160,32,0.12)' : T.bgSubtle,
                color: count === 3 ? T.green : count > 0 ? T.amber : T.fgMuted,
                fontFamily: sharedStyles.fontMono,
                minWidth: 44, textAlign: 'center',
              }}>
                {count}/3
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Aggregate keyword metrics (3-tile card)

function KeywordMetrics({ aiScans, active }: { aiScans: LfAIScan[]; active: string }) {
  const matching = aiScans.filter(s => s.keyword.toLowerCase() === active.toLowerCase());
  const visibility = matching.length
    ? matching.reduce((a, s) => a + (s.saiv ?? 0), 0) / matching.length
    : null;
  const mentions = matching.filter(s => s.mentioned).length;
  const ranks = matching.filter(s => s.mentioned && s.arp != null).map(s => s.arp as number);
  const avgRank = ranks.length ? ranks.reduce((a, b) => a + b, 0) / ranks.length : null;

  const tileStyle: React.CSSProperties = {
    background: '#fff',
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    padding: '14px',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: T.fgMuted,
    marginTop: 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
      <div style={tileStyle}>
        <div style={{
          fontFamily: sharedStyles.fontMono,
          fontSize: 28,
          fontWeight: 700,
          color: visibility == null ? T.fgMuted : visibility >= 50 ? T.green : visibility > 0 ? T.amber : T.magenta,
          lineHeight: 1,
        }}>
          {visibility == null ? '—' : `${visibility.toFixed(0)}%`}
        </div>
        <div style={labelStyle}>Visibility</div>
      </div>
      <div style={tileStyle}>
        <div style={{
          fontFamily: sharedStyles.fontMono,
          fontSize: 28,
          fontWeight: 700,
          color: T.fgPrimary,
          lineHeight: 1,
        }}>
          {mentions}
        </div>
        <div style={labelStyle}>Mentions</div>
      </div>
      <div style={tileStyle}>
        <div style={{
          fontFamily: sharedStyles.fontMono,
          fontSize: 28,
          fontWeight: 700,
          color: T.fgPrimary,
          lineHeight: 1,
        }}>
          {avgRank == null ? '—' : `#${avgRank.toFixed(1)}`}
        </div>
        <div style={labelStyle}>Avg. Rank</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Visibility comparison (bar chart)

function SaivComparison({
  metas, keyword,
}: { metas: { platform: AIPlatformLF; meta: LfAIScan | null }[]; keyword: string }) {
  return (
    <GBICard style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 14, fontWeight: 700, color: T.fgPrimary, marginBottom: 4 }}>
        Visibility by engine
      </div>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 14 }}>
        Visibility across AI engines for "{keyword}"
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {metas.map(({ platform, meta }) => {
          const saiv = meta?.saiv ?? 0;
          const color = platformColor(platform);
          return (
            <div key={platform} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 50px', gap: 10, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: T.fgPrimary, fontWeight: 500 }}>{PLATFORM_LABEL[platform]}</div>
              <div style={{ height: 22, background: T.bgSubtle, borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.max(0.5, saiv)}%`,
                  background: color,
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: 8,
                  fontFamily: sharedStyles.fontMono,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                }}>
                  {saiv > 8 && `${saiv.toFixed(1)}%`}
                </div>
              </div>
              <div style={{
                fontFamily: sharedStyles.fontMono, fontSize: 11, fontWeight: 600,
                color: meta ? T.fgPrimary : T.fgMuted, textAlign: 'right',
              }}>
                {meta ? `${saiv.toFixed(1)}%` : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </GBICard>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Competitors AI named

type CompetitorRow = {
  name: string;
  avgRank: number | null;
  engines: number;
  isClient: boolean;
  /** client row only: the name(s) as the AI actually wrote them */
  aiWrote?: string[];
};

function CompetitorsNamed({
  metas, fullByPlatform, keyword, loading, clientName, placeDisplayName,
}: {
  metas: { platform: AIPlatformLF; meta: LfAIScan | null }[];
  fullByPlatform: Map<AIPlatformLF, LfAIScanFull>;
  keyword: string;
  loading: boolean;
  clientName: string;
  placeDisplayName?: string | null;
}) {
  const enginesLoaded = metas.filter(
    m => m.meta != null && fullByPlatform.has(m.platform),
  ).length;

  // Aggregate competitor ranks and client ranks across platforms.
  const { competitorMap, clientRanks, clientEngines, clientAiNames } = useMemo(() => {
    const map = new Map<string, { name: string; ranks: number[]; engines: Set<AIPlatformLF> }>();
    const cRanks: number[] = [];
    const cEngines = new Set<AIPlatformLF>();
    const cNames: string[] = [];

    for (const { platform, meta } of metas) {
      if (!meta) continue;
      const full = fullByPlatform.get(platform);
      if (!full) continue;
      const lines = normalizeAnalysisLines(full.aiAnalysis?.vulnerable);
      // arp (answer rank position) from stored scan data — preferred source of truth.
      const clientRankHere = (meta.mentioned && meta.arp != null) ? meta.arp : null;
      // Track whether we've already credited the client for this engine (avoid double-count).
      let clientCreditedForEngine = clientRankHere != null;
      if (clientRankHere != null) {
        cRanks.push(clientRankHere);
        cEngines.add(platform);
        // What the AI literally wrote at that rank, so a reader can verify it's
        // really this business (older scans: fall back to the stored list).
        const ranked = Array.isArray(full.aiAnalysis?.competitors) ? (full.aiAnalysis!.competitors as unknown[]).map(String) : [];
        const wrote = full.aiAnalysis?.matchedName ?? ranked[clientRankHere - 1] ?? lines[clientRankHere - 1];
        if (wrote && !cNames.includes(wrote)) cNames.push(wrote);
      }

      lines.forEach((name, i) => {
        const rank = i + 1;

        // Drop junk lines (reason sub-bullets, label prefixes, etc.)
        if (isJunkCompetitorLine(name)) return;

        // Check if this line is the client appearing under any alias.
        const lineIsClient = isClientAlias(name, clientName, placeDisplayName, { rankedNames: lines, keyword });

        // Skip the client's own entry at its exact arp position (existing logic).
        if (clientRankHere != null && rank === clientRankHere) return;

        if (lineIsClient) {
          // Also skip any other alias line for the client in the competitor list.
          // If arp wasn't set for this engine, use the alias line's index to
          // credit the client's rank (fixes existing scan data where arp=null
          // but the client appeared in the list under a Chinese alias).
          if (!clientCreditedForEngine) {
            cRanks.push(rank);
            cEngines.add(platform);
            clientCreditedForEngine = true;
          }
          return;
        }

        const key = name.toLowerCase();
        if (!map.has(key)) {
          map.set(key, { name, ranks: [], engines: new Set() });
        }
        const entry = map.get(key)!;
        entry.ranks.push(rank);
        entry.engines.add(platform);
      });
    }

    return { competitorMap: map, clientRanks: cRanks, clientEngines: cEngines, clientAiNames: cNames };
  }, [metas, fullByPlatform, clientName, placeDisplayName, keyword]);

  const rows = useMemo((): CompetitorRow[] => {
    const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;

    const competitorRows: CompetitorRow[] = Array.from(competitorMap.values()).map(({ name, ranks, engines }) => ({
      name,
      avgRank: ranks.length ? Math.round(mean(ranks)) : null,
      engines: engines.size,
      isClient: false,
    }));

    const clientAvgRank = clientRanks.length ? Math.round(mean(clientRanks)) : null;
    const clientRow: CompetitorRow = {
      name: clientName,
      avgRank: clientAvgRank,
      engines: clientEngines.size,
      isClient: true,
      aiWrote: clientAiNames,
    };

    const allRows = [...competitorRows, clientRow];

    return allRows.sort((a, b) => {
      // Rows with avgRank come before null-rank rows
      if (a.avgRank != null && b.avgRank != null) {
        if (a.avgRank !== b.avgRank) return a.avgRank - b.avgRank;
        if (b.engines !== a.engines) return b.engines - a.engines;
        return a.name.localeCompare(b.name);
      }
      if (a.avgRank != null) return -1;
      if (b.avgRank != null) return 1;
      return 0;
    });
  }, [competitorMap, clientRanks, clientEngines, clientAiNames, clientName]);

  // Always show the client row; only show EmptyHint for competitors section if no competitors loaded
  const hasCompetitors = rows.some(r => !r.isClient);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 16, fontWeight: 700, color: T.fgPrimary, marginBottom: 4 }}>
        Competitors AI named
      </div>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 14 }}>
        Where you rank vs businesses AI recommends for "{keyword}"
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((row) => {
          const rankBadge = row.avgRank != null
            ? `#${row.avgRank}`
            : 'Not ranked';
          const isUnranked = row.avgRank == null;

          return (
            <div
              key={row.isClient ? '__client__' : row.name.toLowerCase()}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                border: row.isClient ? `1.5px solid ${T.cyan}` : `1px solid ${T.border}`,
                borderRadius: 6,
                background: row.isClient ? 'rgba(18,181,196,0.07)' : T.bgSubtle,
              }}
            >
              {/* Rank badge */}
              <span style={{
                fontFamily: sharedStyles.fontMono,
                fontSize: 12,
                fontWeight: 700,
                color: isUnranked ? T.fgMuted : T.fgPrimary,
                minWidth: 72,
                flexShrink: 0,
              }}>
                {rankBadge}
              </span>

              {/* Business name + optional "You" pill */}
              <span style={{
                fontSize: 13,
                color: T.fgPrimary,
                fontWeight: 600,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                overflow: 'hidden',
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.name}
                </span>
                {row.isClient && (
                  <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: T.cyan,
                    background: 'rgba(18,181,196,0.10)',
                    border: `1px solid ${T.cyan}`,
                    borderRadius: 10,
                    padding: '1px 7px',
                    flexShrink: 0,
                  }}>
                    You
                  </span>
                )}
                {row.isClient && row.aiWrote && row.aiWrote.length > 0 && (
                  <span
                    title={row.aiWrote.join(' · ')}
                    style={{ fontSize: 11, fontWeight: 400, color: T.fgSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                  >
                    AI wrote: {row.aiWrote.map(n => `"${n}"`).join(' · ')}
                  </span>
                )}
              </span>

              {/* Engine count badge */}
              <span style={{
                fontSize: 10,
                fontFamily: sharedStyles.fontMono,
                color: T.fgMuted,
                flexShrink: 0,
                marginLeft: 8,
              }}>
                {row.engines}/{enginesLoaded} engines
              </span>
            </div>
          );
        })}
        {loading && !hasCompetitors && (
          <EmptyHint text="Loading competitors…" />
        )}
        {!loading && !hasCompetitors && (
          <EmptyHint text="No competitors named yet" />
        )}
      </div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div style={{ fontSize: 11, color: T.fgMuted, textAlign: 'center', padding: 16 }}>
      {text}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers

const containerStyle = {
  flex: 1, overflowY: 'auto' as const, padding: 28, background: '#f8f9fa',
};

function relativeTime(deltaMs: number): string {
  if (deltaMs < 60_000) return 'just now';
  if (deltaMs < 3_600_000) return `${Math.floor(deltaMs / 60_000)}m ago`;
  if (deltaMs < 86_400_000) return `${Math.floor(deltaMs / 3_600_000)}h ago`;
  const d = Math.floor(deltaMs / 86_400_000);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}
